import { z } from 'zod';

/**
 * O que o daemon lê dos artefatos internos do Claude Code.
 *
 * Nada disto é documentado — o formato foi lido do próprio binário — e é
 * justamente por isso que entra por schema: quando uma versão nova mudar a
 * forma, a falha aparece AQUI, nomeada, em vez de virar um `undefined` que
 * atravessa o daemon até quebrar longe da causa.
 */

/** `~/.claude/sessions/<pid>.json`, publicado por toda sessão viva. */
export const RegistroDeSessaoLocalSchema = z
  .object({
    pid: z.number().int().positive(),
    sessionId: z.string().min(1),
    cwd: z.string().optional(),
    messagingSocketPath: z.string().min(1).optional(),
    /** Carimbo de início do processo: a defesa real contra reúso de PID. */
    procStart: z.string().optional(),
    pidDomain: z.string().optional(),
    kind: z.string().optional(),
    entrypoint: z.string().optional(),
    version: z.string().optional(),
    /** A âncora do autofixável: muda quando o QUADRO muda, não a cada patch. */
    peerProtocol: z.number().int().optional(),
    peerFeatures: z.array(z.string()).optional(),
  })
  .loose();
export type RegistroDeSessaoLocal = z.infer<typeof RegistroDeSessaoLocalSchema>;

/** `<pid>.<hash de 64 hex>.key`, ao lado do registro. */
export const REGEX_CHAVE_DE_SESSAO = /^(\d+)\.[0-9a-f]{64}\.key$/;

export const ChaveDeSessaoLocalSchema = z
  .object({
    peerToken: z.string().min(1),
    procStartFt: z.string().optional(),
  })
  .loose();

/**
 * O que de fato aconteceu com uma mensagem mandada pelo canal local.
 *
 * O `{ok:true}` cru de antes mentia: o Claude Code retém, recusa e descarta
 * mensagens de peer (`peer_message_status`), e sem caixa de entrada nossa a
 * única verdade disponível é `sem_confirmacao` — dita com essas palavras.
 */
export const RespostaDaEntregaSchema = z.object({
  ok: z.boolean(),
  estado: z.enum(['entregue', 'retida', 'recusada', 'expirada', 'descartada', 'sem_confirmacao']),
  motivo: z.string().max(300).optional(),
});
export type RespostaDaEntrega = z.infer<typeof RespostaDaEntregaSchema>;

/** Relatório da sonda: o que esta máquina consegue fazer, com motivo. */
export const EstadoDeCapacidadeSchema = z.object({
  estado: z.enum(['aprovada', 'reprovada', 'nao_verificada', 'nao_suportada']),
  /** Texto de tela, em português: vira o `title` do botão desabilitado. */
  motivo: z.string().max(300).default(''),
});
export type EstadoDeCapacidade = z.infer<typeof EstadoDeCapacidadeSchema>;

export const RelatorioDeCapacidadesSchema = z.object({
  versaoClaude: z.string().nullable(),
  peerProtocolo: z.number().int().nullable(),
  verificadoEm: z.iso.datetime(),
  capacidades: z.record(z.string().max(60), EstadoDeCapacidadeSchema),
});
export type RelatorioDeCapacidades = z.infer<typeof RelatorioDeCapacidadesSchema>;
